import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import { fetchMyCashShift, formatCurrency } from "../../services/cashService";
import styles from "../shifts/shifts.module.scss";

const CashRegister = () => {
    const { onShift, shift } = useSelector((s) => s.activeShiftSlice);
    const isInStoreShift = onShift && shift?.workLocation === "in_store";

    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(null);
    const [transactions, setTransactions] = useState([]);

    const loadBalance = async () => {
        setLoading(true);
        const res = await fetchMyCashShift();
        if (res?.success) {
            setBalance(res.data?.balance || null);
            setTransactions(res.data?.transactions || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (isInStoreShift) loadBalance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInStoreShift]);

    const amountBody = (row) => `${row.type === "out" ? "-" : "+"}${formatCurrency(row.amount, row.currency)}`;
    const dateBody = (row) => (row.createdAt ? new Date(row.createdAt).toLocaleString() : "-");
    const typeBody = (row) => <span className={`${styles.badge} ${row.type === "out" ? styles.badgePending : styles.badgeActive}`}>{row.type === "out" ? "Cash Out" : "Cash In"}</span>;
    const invoiceBody = (row) => row.invoiceId?.invoiceId || row.invoiceId?.Waybills || "-";
    // Every entry here is already against THIS shift's register - this only
    // fires when someone other than the clerk on it (an admin recording a
    // payment, a handler cashing out, etc.) is who actually recorded it.
    const recordedByBody = (row) =>
        row.recordedByOtherHolder ? (
            <span className={styles.otherHolderTag} title={`${row.recordedByName} recorded this against your register while you were on shift.`}>
                <i className="pi pi-users" /> {row.recordedByName}
            </span>
        ) : (
            "You"
        );

    if (!isInStoreShift) {
        return (
            <div className={styles.page}>
                <div className={styles.pageHeader}>
                    <div className={styles.headerTitleRow}>
                        <div className={styles.headerIcon}>
                            <i className="pi pi-wallet" />
                        </div>
                        <div>
                            <h2>Cash Register</h2>
                            <p className={styles.subtitle}>Available only during an active in-store shift</p>
                        </div>
                    </div>
                </div>
                <div className={styles.sectionCard}>
                    <p className={styles.shiftSub}>You need to be clocked in with an "In Store" shift to use the cash register.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-wallet" />
                    </div>
                    <div>
                        <h2>Cash Register</h2>
                        <p className={styles.subtitle}>Cash movement recorded against your register during this shift</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading cash register...</span>
                </div>
            ) : (
                <>
                    <div className={styles.statsRow}>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-lock" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.startingCash?.usd)}</div>
                                <div className={styles.statLabel}>Starting Cash (USD)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-lock" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.startingCash?.jmd, "jmd")}</div>
                                <div className={styles.statLabel}>Starting Cash (JMD)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-down" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.cashIn?.usd)}</div>
                                <div className={styles.statLabel}>Cash In (USD)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-down" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.cashIn?.jmd, "jmd")}</div>
                                <div className={styles.statLabel}>Cash In (JMD)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-up" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.cashOut?.usd)}</div>
                                <div className={styles.statLabel}>Cash Out (USD)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-up" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.cashOut?.jmd, "jmd")}</div>
                                <div className={styles.statLabel}>Cash Out (JMD)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-wallet" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.balance?.usd)}</div>
                                <div className={styles.statLabel}>Current Balance (USD)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-wallet" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatCurrency(balance?.balance?.jmd, "jmd")}</div>
                                <div className={styles.statLabel}>Current Balance (JMD)</div>
                            </div>
                        </div>
                    </div>

                    <ResponsiveView
                        value={transactions}
                        emptyMessage="No cash transactions recorded yet."
                        storageKey="cashRegister.transactions"
                        renderCard={(row) => (
                            <DataCard
                                key={row._id || row.createdAt}
                                accent={row.type === "out" ? "amber" : "green"}
                                title={amountBody(row)}
                                subtitle={dateBody(row)}
                                badge={typeBody(row)}
                                fields={[
                                    { label: "Recorded By", value: recordedByBody(row) },
                                    { label: "Reason", value: row.reason },
                                    { label: "Invoice", value: invoiceBody(row) },
                                ]}
                            />
                        )}
                    >
                        <DataTable
                            value={transactions}
                            paginator
                            rows={20}
                            className="datatable-responsive custom-datatable"
                            emptyMessage="No cash transactions recorded yet."
                            rowHover
                            rowClassName={(row) => (row.recordedByOtherHolder ? styles.otherHolderRow : "")}
                        >
                            <Column header="Date" body={dateBody} />
                            <Column header="Type" body={typeBody} />
                            <Column header="Amount" body={amountBody} />
                            <Column header="Recorded By" body={recordedByBody} />
                            <Column field="reason" header="Reason" />
                            <Column header="Linked Invoice" body={invoiceBody} />
                        </DataTable>
                    </ResponsiveView>
                </>
            )}
        </div>
    );
};

export default CashRegister;
